import {
  gearboxAccountCompressor,
  gearboxMarketConfigurators,
} from '@1delta/data-sdk'
import { zeroAddress } from 'viem'
import { Call } from '../../../utils/multicall'

/**
 * Page size for `CreditAccountCompressor.getCreditAccounts` — 128 covers
 * nearly every borrower (a single EOA rarely holds more than a handful of
 * CAs across all curators on a chain). If we ever hit this ceiling the
 * parser can iterate `nextOffset` in a follow-up call; not wired yet.
 */
const CA_QUERY_LIMIT = 128

/**
 * Number of multicall slots consumed by one user-data fetch for Gearbox V3.1:
 * one `getCreditAccounts(cmFilter, caFilter, offset, limit)` call that
 * returns every Credit Account the borrower owns across all known curators.
 */
export const GEARBOX_V3_USER_CALL_COUNT = 1

/**
 * Build the user-data multicall descriptor for Gearbox V3.1.
 *
 * Single call: `CreditAccountCompressor.getCreditAccounts(cmFilter,
 * caFilter, 0, 128)` with
 *   - `cmFilter.configurators` = every curator registered for this chain
 *     in `gearbox-resolvers.json`
 *   - `caFilter.owner`         = the borrower address
 *
 * Returns `(CreditAccountData[], uint256 nextOffset)`. The parser keeps
 * the CA array and ignores `nextOffset` — see `CA_QUERY_LIMIT` for the
 * pagination caveat.
 */
export const buildGearboxV3UserCall = (
  chainId: string,
  _lender: string,
  account: string,
): Call[] => {
  const compressor = gearboxAccountCompressor()
  const configurators = gearboxMarketConfigurators(chainId)
  if (!compressor || !configurators) return []

  const curatorAddrs = Object.keys(configurators)
  if (curatorAddrs.length === 0) return []

  return [
    {
      address: compressor,
      name: 'getCreditAccounts',
      params: [
        {
          configurators: curatorAddrs,
          creditManagers: [] as string[],
          pools: [] as string[],
          underlying: zeroAddress,
        },
        {
          owner: account,
          // Include even 0-debt accounts (user may hold collateral with no
          // borrow), and don't filter by health factor.
          includeZeroDebt: true,
          minHealthFactor: 0n,
          maxHealthFactor: (1n << 256n) - 1n,
          // `reverting: false` → skip CAs whose data fetch reverted
          // rather than returning `success: false` stubs. We can flip
          // this later if we want to surface malformed CAs to the UI.
          reverting: false,
        },
        0n,
        BigInt(CA_QUERY_LIMIT),
      ],
    },
  ]
}
